package com.zq.webproject.network.model

import kotlinx.serialization.Serializable

/**
 * 用户信息数据模型
 * 根据 GET /users 接口返回的实际数据结构定义
 */
@Serializable
data class User(
    val id: Int,
    val username: String?=null,
    val password: String? = null,       // 密码（接口返回中包含，实际使用时注意安全）
    val balance: Double = 0.0,          // 账户余额
    val referralCode: String? = null,   // 推荐码
    val referredBy: Int? = null,        // 推荐人ID（可为null）
    val phone: String? = null,          // 手机号
    val email: String? = null           // 邮箱（保留兼容性）
)

/**
 * 登录请求参数
 */
@Serializable
data class LoginRequest(
    val phone: String,
    val password: String
)

/**
 * 登录返回的用户信息
 * 注意：登录接口返回的user结构与获取用户列表不同
 */
@Serializable
data class LoginUser(
    val phoneNumber: String? = null,
    val balance: Double = 0.0,
    val referralCode: String? = null,
    val referredBy: Int? = null
)

/**
 * 登录响应
 * POST /users/login 接口返回格式
 */
@Serializable
data class LoginResponse(
    val code: Int,
    val message: String? = null,
    val data: DataBen? = null
)
@Serializable
data class DataBen(
    val token: String? = null,
    val user: LoginUser? = null
)
//{
//    "code": 200,
//    "message": "登录成功",
//    "data": {
//    "user": {
//        "id": 1,
//        "phoneNumber": "15093137202",
//        "balance": 1417.5,
//        "referralCode": "123456",
//        "referredBy": 1
//    },
//    "token": "9508254b-6c86-4a2d-9550-5fd25f172d6d"
//}
//}

/**
 * 发送验证码响应
 * POST /users/sendCode 接口返回格式
 */
@Serializable
data class SendCodeResponse(
    val code: Int,
    val message: String? = null,
    val data: String? = null
)

/**
 * 注册返回的数据
 */
@Serializable
data class RegisterData(
    val userId: Int? = null
)

/**
 * 注册响应
 * POST /users/register 接口返回格式
 */
@Serializable
data class RegisterResponse(
    val code: Int,
    val message: String? = null,
    val data: RegisterData? = null  // 注册成功返回的数据对象
)

/**
 * 用户列表响应
 * GET /users 接口返回格式
 */
@Serializable
data class UserListResponse(
    val success: Boolean,
    val data: List<User>
)

/**
 * 获取用户余额响应
 * GET /users/balance 接口返回格式
 * 返回示例: {"code":200,"message":"获取成功","data":{"balance":1417.5}}
 */
@Serializable
data class BalanceResponse(
    val code: Int,
    val message: String? = null,
    val data: BalanceData? = null
)

@Serializable
data class BalanceData(
    val balance: Double
)

/**
 * 升级为加盟商响应
 * POST /users/upgrade-to-affiliate 接口返回格式
 * 余额支付返回示例: {"code":200,"message":"升级成功","data":null}
 * 支付宝支付返回示例: {"code":200,"message":"订单创建成功","data":{"img":"...","payurl":"...","orderNo":"..."}}
 */
@Serializable
data class UpgradeResponse(
    val code: Int,
    val message: String? = null,
    val msg: String? = null,  // 兼容易支付格式
    val data: UpgradeData? = null,
    // 支持顶层字段（易支付格式）
    val img: String? = null,
    val payurl: String? = null,
    val orderNo: String? = null
)

/**
 * 升级支付数据（支付宝支付时返回）
 */
@Serializable
data class UpgradeData(
    val img: String? = null,        // 二维码图片URL
    val payurl: String? = null,     // 支付链接
    val orderNo: String? = null     // 订单号
)

/**
 * 获取用户角色响应
 * GET /users/role 接口返回格式
 * 返回示例: {"code":200,"message":"获取成功","data":{"role":"user"}}
 * 角色类型: user(普通用户) | affiliate(加盟商) | admin(管理员)
 */
@Serializable
data class UserRoleResponse(
    val code: Int,
    val message: String? = null,
    val data: UserRoleData? = null
)

@Serializable
data class UserRoleData(
    val role: String
)

/**
 * 获取当前用户信息响应
 * GET /users/me 接口返回格式
 * 返回示例: {"code":200,"message":"获取成功","data":{"id":1,"phoneNumber":"15093137202","balance":1417.5,"referralCode":"123456","referredBy":1}}
 */
@Serializable
data class CurrentUserResponse(
    val code: Int,
    val message: String? = null,
    val data: CurrentUserData? = null
)

@Serializable
data class CurrentUserData(
    val id: Int,
    val phoneNumber: String,
    val balance: Double,
    val referralCode: String,
    val referredBy: Int? = null
)

/**
 * 获取用户总收益响应
 * GET /users/earnings 接口返回格式
 * 返回示例: {"code":200,"message":"获取成功","data":{"totalEarnings":123.45}}
 */
@Serializable
data class EarningsResponse(
    val code: Int,
    val message: String? = null,
    val data: EarningsData? = null
)

@Serializable
data class EarningsData(
    val totalEarnings: Double
)

/**
 * 充值响应
 * POST /users/recharge 接口返回格式
 * 支付宝支付返回示例: {"code":200,"message":"订单创建成功","data":{"img":"...","payurl":"...","orderNo":"..."}}
 */
@Serializable
data class RechargeResponse(
    val code: Int,
    val message: String? = null,
    val msg: String? = null,  // 兼容易支付格式
    val data: RechargeData? = null,
    // 支持顶层字段（易支付格式）
    val img: String? = null,
    val qrcode: String? = null,     // 支付宝二维码原始链接
    val payurl: String? = null,
    val orderNo: String? = null
)

/**
 * 充值支付数据（支付宝支付时返回）
 */
@Serializable
data class RechargeData(
    val img: String? = null,        // 二维码图片URL
    val qrcode: String? = null,     // 支付宝二维码原始链接
    val payurl: String? = null,     // 支付链接
    val orderNo: String? = null     // 订单号
)

/**
 * 上传商品请求
 * POST /products/upload 接口请求格式
 */
@Serializable
data class UploadProductRequest(
    val name: String,
    val linkUrl: String,
    val category: String,
    val remark: String? = null,
    val description: String? = null,
    val coverImageUrl: String? = null,
    val price: Double = 0.0,
    val status: Int = 0,
    val tags: String? = null,
    val sortOrder: Int = 0
)

/**
 * 上传商品响应
 * POST /products/upload 接口返回格式
 * 返回示例: {"code":200,"message":"商品上传成功","data":{"productId":123}}
 */
@Serializable
data class UploadProductResponse(
    val code: Int,
    val message: String? = null,
    val data: UploadProductData? = null
)

/**
 * 上传商品返回的数据
 */
@Serializable
data class UploadProductData(
    val productId: Int
)